/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import {getTestProps} from '@benchling/js-test-utils';
import moment from 'moment';
import React from 'react';
import _ from 'underscore';

import * as OnboardingActions from 'actions/OnboardingActions';
import {openSampleEntry, openSampleSequence} from 'actions/OnboardingActions';
// @ts-expect-error
import * as UserActions from 'actions/UserActions';
import {trackEvent} from 'analytics/Analytics';
import {ACTION_CATEGORY, ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {ProcessesFeature, RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import CalendarActions from 'calendar/CalendarActions';
import CalendarStore from 'calendar/CalendarStore';
import EntryCalendar from 'calendar/EntryCalendar';
import DataConnector from 'connect/DataConnector';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import OnboardingConstants from 'constants/OnboardingConstants';
import * as SpaceUtil from 'growth/SpaceUtil';
import HomeDashboardActions from 'home-dashboard/HomeDashboardActions';
import HomeDashboardStore from 'home-dashboard/HomeDashboardStore';
import ChecklistPanelLink from 'onboarding/ChecklistPanelLink';
import GettingStartedPanel from 'onboarding/GettingStartedPanel';
import {
  ENTRY_CHECKLIST_KEY,
  NUEModuleEnhancedProps,
  SEQUENCE_CHECKLIST_KEY,
} from 'onboarding/OnboardingConstants';
import {trackChecklistOpened} from 'onboarding/OnboardingUtils';
import AssignedProcessTaskStatusSummary from 'processes/home/AssignedProcessTaskStatusSummary';
import EntityStore from 'stores/EntityStore';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as nyc from 'ui-library/NYC';
import licenses from 'user/Licenses';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';
import RecentWorklistsHomeListing from 'worklists/sidepanel-components/RecentWorklistsHomeListing';

import FileSummary from '../home/FileSummary';
import ReviewSummary from '../home/ReviewSummary';
import ToggleDropdown from '../ToggleDropdown';
import SidepanelNotificationsSection from './home/SidepanelNotificationsSection';
import HomeSidepanelSignedOut from './HomeSidepanelSignedOut';
import * as SidepanelActions from './SidepanelActions';

class HomeSidepanelConnector extends DataConnector<BaseHomeSidepanelProps, EnhancedProps> {
  override calendarStore: any;
  override entityStore: any;
  override homeDashboardStore: any;
  override shouldSubscribeToRedux = false;
  override stores = [CalendarStore, EntityStore, HomeDashboardStore];

  enhanceProps(): EnhancedProps {
    const todayDate = this.calendarStore().getToday();
    const files = this.calendarStore().getFiles()[todayDate.format('YYYY-MM-DD')] || [];
    const entryModule = HomeDashboardStore.getNUEModuleState(OnboardingConstants.MODULES.ENTRY);
    const sequenceModule = HomeDashboardStore.getNUEModuleState(OnboardingConstants.MODULES.SEQUENCE);
    return {
      files,
      todayDate,
      currentUser: this.entityStore().currentUser(),
      gettingStarted: this.homeDashboardStore().getNUEModuleState(
        OnboardingConstants.MODULES.GETTING_STARTED
      ),
      hasIncompleteEntryChecklist: (entryModule && !entryModule.completed) ?? false,
      hasIncompleteSequenceChecklist: (sequenceModule && !sequenceModule.completed) ?? false,
      hasGettingStartedSpace: this.homeDashboardStore().getHasGettingStartedSpace(),
      entryReviewSummary: this.homeDashboardStore().getEntryReviewSummary(),
      worksheetReviewSummary: this.homeDashboardStore().getWorksheetReviewSummary(),
    };
  }
}

export interface BaseHomeSidepanelProps {
  activeItemId?: string | null;
  isExpanded: boolean;
}

export interface ReviewSummaryProps {
  rejected: number;
  needsReview: number;
  sentForReview: number;
  accepted: number;
  inProgress: number;
  retracted: number;
}

interface EnhancedProps {
  files: Array<{
    endAccess?: string;
    id: string;
    name: string;
  }>;
  todayDate: moment.Moment;
  currentUser?: EntityStoreUser;
  gettingStarted?: NUEModuleEnhancedProps;
  hasIncompleteEntryChecklist?: boolean;
  hasIncompleteSequenceChecklist?: boolean;
  hasGettingStartedSpace?: boolean;
  entryReviewSummary: ReviewSummaryProps;
  worksheetReviewSummary: ReviewSummaryProps;
}

class HomeSidepanel extends React.Component<BaseHomeSidepanelProps & EnhancedProps> {
  override componentDidMount(): void {
    if (!this.props.currentUser) {
      return;
    }
    // Browsers cap the number of simultaneous network requests to a given domain - typically around 6.
    // <Sidepanel> (which renders this component) does a few expensive GraphQL queries, and since Apollo
    // always defers queries for a tick, if we don't match that behavior here, these network requests are
    // fired first, and ultimately make page load slower (we want the slow network requests to start as soon
    // as possible).
    _.defer(() => {
      if (licenses.hasLicense('LICENSE_REVIEWS')) {
        HomeDashboardActions.fetchEntryReviewSummary();
        HomeDashboardActions.fetchWorksheetReviewSummary();
      }
      UserActions.loadOrgRequests();
      const todayDate = CalendarStore.getToday();
      const filesEndDate = todayDate.clone().add(2, 'days');
      CalendarActions.fetchFiles(todayDate, filesEndDate);
      if (BenchlingConfig.ENABLE_NUE_MODULES || BenchlingConfig.ENABLE_GETTING_STARTED_FLOW) {
        OnboardingActions.fetchOnboardingTasks();
      }
    });
  }

  _handleExpand = (): void => {
    SidepanelActions.expand();
  };

  override render(): JSX.Element {
    const hasIncompleteTasks = this.props.gettingStarted?.tasks?.some((task) => !task.completed);
    const shouldShowOnboardingChecklistLinks = BenchlingConfig.ENABLE_NUE_MODULES;
    return (
      <nyc.Container
        className="sidepanel"
        size={this.props.isExpanded ? 'lg' : 'sidepanel'}
        dataTestAttrs={{
          component: 'HomeSidepanel',
          element: 'root',
          key: this.props.isExpanded ? 'expanded' : 'collapsed',
        }}
      >
        <nyc.Row className="homeSidepanel js-navTour-home">
          <nyc.Container className="homeSidepanel-today" size="sidepanel">
            {!this.props.currentUser ? (
              <HomeSidepanelSignedOut />
            ) : (
              <div>
                <nyc.Row>
                  <nyc.Col sm={6}>
                    <div className="homeSidepanel-today-header u-lr-split">
                      <div
                        className="homeSidepanel-today-date whiteLink"
                        onClick={this._handleExpand}
                        {...getTestProps({
                          component: 'HomeSidepanel',
                          element: 'todayDate',
                        })}
                      >
                        <div className="homeSidepanel-today-date-month">
                          {this.props.todayDate.format('MMMM')}
                        </div>
                        <div className="homeSidepanel-today-date-day">
                          {this.props.todayDate.format('DD')}
                        </div>
                      </div>
                      {!this.props.isExpanded && (
                        <div>
                          <ToggleDropdown
                            expanded={false}
                            buttonStyle={ButtonStyle.TERTIARY_WHITE_TRANSPARENT}
                            onToggle={(expanded) => {
                              trackEvent({
                                module: ANALYTICS_MODULE.NAVIGATION,
                                actionCategory: ACTION_CATEGORY.READ,
                                action: `home_sidepanel_${expanded ? 'expand' : 'collapse'}`,
                              });
                            }}
                            onClose={() => {
                              trackEvent({
                                module: ANALYTICS_MODULE.NAVIGATION,
                                actionCategory: ACTION_CATEGORY.READ,
                                action: 'home_sidepanel_close',
                              });
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </nyc.Col>
                </nyc.Row>
                {licenses.hasLicense('LICENSE_REVIEWS') && !_.isEmpty(this.props.entryReviewSummary) && (
                  <nyc.Row>
                    <nyc.Container
                      size="sidepanel"
                      className="homeSidepanel-today-itemSummary homeSidepanel-review-summary"
                    >
                      <nyc.Row className="homeSidepanel-today-sectionHeader">
                        <div className="homeSidepanel-today-sectionHeader-content">My entry to-dos</div>
                      </nyc.Row>

                      <nyc.Row>
                        <nyc.Col sm={6}>
                          <ReviewSummary
                            reviewableType="entry"
                            reviewSummary={this.props.entryReviewSummary}
                          />
                        </nyc.Col>
                      </nyc.Row>
                    </nyc.Container>
                  </nyc.Row>
                )}
                {licenses.hasLicense('LICENSE_REVIEWS') && !_.isEmpty(this.props.worksheetReviewSummary) && (
                  <nyc.Row>
                    <nyc.Container
                      size="sidepanel"
                      className="homeSidepanel-today-itemSummary homeSidepanel-review-summary"
                    >
                      <nyc.Row className="homeSidepanel-today-sectionHeader">
                        <div className="homeSidepanel-today-sectionHeader-content">My worksheet to-dos</div>
                      </nyc.Row>

                      <nyc.Row>
                        <nyc.Col sm={6}>
                          <ReviewSummary
                            reviewableType="worksheet"
                            reviewSummary={this.props.worksheetReviewSummary}
                          />
                        </nyc.Col>
                      </nyc.Row>
                    </nyc.Container>
                  </nyc.Row>
                )}
                {isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}) &&
                  isModelTypeEnabledForListing(ModelTypeConstants.PROCESS_TASK) && (
                    <AssignedProcessTaskStatusSummary />
                  )}
                {BenchlingConfig.ENABLE_GETTING_STARTED_FLOW && hasIncompleteTasks && (
                  <nyc.Row>
                    <nyc.Col sm={6}>
                      <GettingStartedPanel
                        currentUser={this.props.currentUser}
                        tasks={this.props.gettingStarted!.tasks!}
                        completed={this.props.gettingStarted!.completed}
                        hasSpace={
                          this.props.hasGettingStartedSpace ||
                          SpaceUtil.hasUnlimitedSpace(this.props.currentUser)
                        }
                      />
                    </nyc.Col>
                  </nyc.Row>
                )}
                {shouldShowOnboardingChecklistLinks && (
                  <>
                    {this.props.hasIncompleteSequenceChecklist && (
                      <nyc.Row>
                        <nyc.Col sm={6}>
                          <ChecklistPanelLink
                            // @ts-expect-error ts-migrate FIXME
                            checklistKey={SEQUENCE_CHECKLIST_KEY}
                            displayText="Explore Molecular Biology"
                            onClick={() => {
                              trackChecklistOpened(SEQUENCE_CHECKLIST_KEY, 'sidepanel');
                              openSampleSequence();
                            }}
                          />
                        </nyc.Col>
                      </nyc.Row>
                    )}
                    {this.props.hasIncompleteEntryChecklist && (
                      <nyc.Row>
                        <nyc.Col sm={6}>
                          <ChecklistPanelLink
                            displayText="Explore the Notebook"
                            onClick={() => {
                              trackChecklistOpened(ENTRY_CHECKLIST_KEY, 'sidepanel');
                              openSampleEntry();
                            }}
                          />
                        </nyc.Col>
                      </nyc.Row>
                    )}
                  </>
                )}
                <nyc.Row>
                  <FileSummary
                    emptyText="You have no recently opened entities."
                    files={this.props.files}
                    headerText="Recently opened"
                    activeItemId={this.props.activeItemId}
                  />
                </nyc.Row>
                {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS &&
                  isFeatureEnabled({feature: RegistryFeature.REGISTRY_WORKLISTS}) && (
                    <nyc.Row>
                      <RecentWorklistsHomeListing />
                    </nyc.Row>
                  )}
                <SidepanelNotificationsSection currentUser={this.props.currentUser} />
              </div>
            )}
          </nyc.Container>

          {this.props.isExpanded && (
            <nyc.Container size="md" className="homeSidepanel-calendar-container expandedContainer">
              <nyc.Row>
                <nyc.Col md={16} className="expandedBrowser-toggleCollapseArrowWrapper">
                  <ToggleDropdown className="js-navTour-calendar" expanded={true} />
                </nyc.Col>
              </nyc.Row>
              <div className="homeSidepanel-calendar">
                <EntryCalendar />
              </div>
            </nyc.Container>
          )}
        </nyc.Row>
      </nyc.Container>
    );
  }
}

export default HomeSidepanelConnector.connect(HomeSidepanel, {
  throttleUpdates: true,
});
